import { Injectable } from '@angular/core';
import { Router, NavigationExtras } from "@angular/router";
import { HttpClient } from '@angular/common/http';
import { Observable, Subject, forkJoin } from 'rxjs';
import { mergeMap, tap } from 'rxjs/operators';


import { AccountEndpoint } from './account-endpoint.service';
import { AuthService } from './auth.service';
import { User } from '../models/user.model';
import { Role } from '../models/role.model';
import { Permission, PermissionNames, PermissionValues } from '../models/permission.model';
import { UserEdit } from '../models/user-edit.model';
import { RoleLabelMap, Roles } from "../models/enums";
import { ODPProjectWorkflowViewModel } from '../models/odpprojectworkflow/odpProjectWorkflow.model';
import { TeamSelectionViewModel } from '../models/team/team.model';
import { LoginConfig } from '../models/login-config.model';



export type RolesChangedOperation = "add" | "delete" | "modify";
export type RolesChangedEventArg = { roles: Role[] | string[], operation: RolesChangedOperation };


@Injectable({ providedIn: 'root'})
export class AccountService {

  public static readonly roleAddedOperation: RolesChangedOperation = "add";
  public static readonly roleDeletedOperation: RolesChangedOperation = "delete";
  public static readonly roleModifiedOperation: RolesChangedOperation = "modify";

  private _rolesChanged = new Subject<RolesChangedEventArg>();


  constructor(private router: Router, private http: HttpClient, private authService: AuthService,
    private accountEndpoint: AccountEndpoint) {

  }


  getUser(userId?: string) {
    return this.accountEndpoint.getUserEndpoint<User>(userId);
  }

  getUserSelf() {
    return this.accountEndpoint.getUserSelfEndpoint<User>();
  }



  getUserAndRoles(userId?: string) {

    return forkJoin(
      this.accountEndpoint.getUserEndpoint<User>(userId),
      this.accountEndpoint.getRolesEndpoint<Role[]>());
  }

  getUsers(page?: number, pageSize?: number) {

    return this.accountEndpoint.getUsersEndpoint<User[]>(page, pageSize);
  }

  getUsersAndRoles(page?: number, pageSize?: number) {

    return forkJoin(
      this.accountEndpoint.getUsersEndpoint<User[]>(page, pageSize),
      this.accountEndpoint.getRolesEndpoint<Role[]>());
  }


  updateUser(user: UserEdit, activeRole: Roles) {
    if (user.id) {
      return this.accountEndpoint.getUpdateUserEndpoint<User>(user, activeRole, user.id);
    } else {
      return this.accountEndpoint.getUserByUserNameEndpoint<User>(user.userName).pipe<User>(
        mergeMap(foundUser => {
          user.id = foundUser.id;
          return this.accountEndpoint.getUpdateUserEndpoint<User>(user, activeRole, user.id)
        }));
    }
  }


  newUser(user: UserEdit) {
    return this.accountEndpoint.getNewUserEndpoint<User>(user);
  }

  resetUserPassword(userEmail: string) {
    return this.accountEndpoint.getResetUserPasswordEndpoint<boolean>(userEmail);
  }



  //getUserPreferences() {
  //  return this.accountEndpoint.getUserPreferencesEndpoint<string>();
  //}

  //updateUserPreferences(configuration: string) {
  //  return this.accountEndpoint.getUpdateUserPreferencesEndpoint(configuration);
  //}


  //deleteUser(userOrUserId: string | UserEdit): Observable<User> {

  //  if (typeof userOrUserId === 'string' || userOrUserId instanceof String) {
  //    return this.accountEndpoint.getDeleteUserEndpoint<User>(<string>userOrUserId).pipe<User>(
  //      tap(data => this.onRolesUserCountChanged(data.roles)));
  //  }
  //  else {

  //    if (userOrUserId.id) {
  //      return this.deleteUser(userOrUserId.id);
  //    }
  //    else {
  //      return this.accountEndpoint.getUserByUserNameEndpoint<User>(userOrUserId.userName).pipe<User>(
  //        mergeMap(user => this.deleteUser(user.id)));
  //    }
  //  }
  //}


  unblockUser(userId: string) {
    return this.accountEndpoint.getUnblockUserEndpoint(userId);
  }


  userHasPermission(permissionValue: PermissionValues): boolean {
    return this.permissions.some(p => p == permissionValue);
  }


  refreshLoggedInUser() {
    return this.authService.refreshLoginOnce();
  }


 /**
 * Return list of role labels based on the roles assigned to the user.
 * Perhaps this should go in a service file somewhere?
 * @param roles - array of roles assigned to the user
 * @returns array of role labels for display
 */
  getRoleLabels(roles) {
    const roleLabels = [];
    for (const role of roles) {
      roleLabels.push(RoleLabelMap[role.toLowerCase()]);
    }
    return roleLabels;
  }

  getRoles(page?: number, pageSize?: number) {

    return this.accountEndpoint.getRolesEndpoint<Role[]>(page, pageSize);
  }


  getRolesAndPermissions(page?: number, pageSize?: number) {

    return forkJoin(
      this.accountEndpoint.getRolesEndpoint<Role[]>(page, pageSize),
      this.accountEndpoint.getPermissionsEndpoint<Permission[]>());
  }


  updateRole(role: Role) {
    if (role.id) {
      return this.accountEndpoint.getUpdateRoleEndpoint(role, role.id).pipe(
        tap(data => this.onRolesChanged([role], AccountService.roleModifiedOperation)));
    }
    else {
      return this.accountEndpoint.getRoleByRoleNameEndpoint<Role>(role.name).pipe(
        mergeMap(foundRole => {
          role.id = foundRole.id;
          return this.accountEndpoint.getUpdateRoleEndpoint(role, role.id)
        }),
        tap(data => this.onRolesChanged([role], AccountService.roleModifiedOperation)));
    }
  }


  newRole(role: Role) {
    return this.accountEndpoint.getNewRoleEndpoint<Role>(role).pipe<Role>(
      tap(data => this.onRolesChanged([role], AccountService.roleAddedOperation)));
  }


  deleteRole(roleOrRoleId: string | Role): Observable<Role> {

    if (typeof roleOrRoleId === 'string' || roleOrRoleId instanceof String) {
      return this.accountEndpoint.getDeleteRoleEndpoint<Role>(<string>roleOrRoleId).pipe<Role>(
        tap(data => this.onRolesChanged([data], AccountService.roleDeletedOperation)));
    }
    else {

      if (roleOrRoleId.id) {
        return this.deleteRole(roleOrRoleId.id);
      }
      else {
        return this.accountEndpoint.getRoleByRoleNameEndpoint<Role>(roleOrRoleId.name).pipe<Role>(
          mergeMap(role => this.deleteRole(role.id)));
      }
    }
  }

  getPermissions() {

    return this.accountEndpoint.getPermissionsEndpoint<Permission[]>();
  }


  private onRolesChanged(roles: Role[] | string[], op: RolesChangedOperation) {
    this._rolesChanged.next({ roles: roles, operation: op });
  }


  onRolesUserCountChanged(roles: Role[] | string[]) {
    return this.onRolesChanged(roles, AccountService.roleModifiedOperation);
  }


  getRolesChangedEvent(): Observable<RolesChangedEventArg> {
    return this._rolesChanged.asObservable();
  }



  get permissions(): PermissionValues[] {
    return this.authService.userPermissions;
  }

  get currentUser() {
    return this.authService.currentUser;
  }

  GetCurrentLoggedUserId() {
    return this.currentUser.id;
  }

  GetCurrentUserName() {
    return this.currentUser.userName;
  }

  GetCurrentUserFullName() {
    return this.currentUser.fullName;
  }
  IsAdminRole() {
    return this.currentUser.roles.some(this.checkAdmin) && this.GetActiveRole() == Roles.Admin;
  }
  IsOdpSupervisorRole() {
    return this.currentUser.roles.some(this.checkOdpSupervisor) && this.GetActiveRole() == Roles.ReconciliationSupervisor;
  }
  IsOdpStaffRole() {
    return this.currentUser.roles.some(this.checkOdpStaff) && this.GetActiveRole() == Roles.ReconciliationCoder;
  }
  IsCoderSupervisorRole() {
    return this.currentUser.roles.some(this.checkCoderSupervisor) && this.GetActiveRole() == Roles.QCSupervisor;
  }
  IsCoderRole() {
    return this.currentUser.roles.some(this.checkCoder) && this.GetActiveRole() == Roles.QCCoder;
  }


  checkAdmin(role) {
    return role.toLowerCase() === Roles[Roles.Admin].toLowerCase();
  }
  checkOdpSupervisor(role) {
    return role.toLowerCase() === Roles[Roles.ReconciliationSupervisor].toLowerCase();
  }
  checkOdpStaff(role) {
    return role.toLowerCase() === Roles[Roles.ReconciliationCoder].toLowerCase();
  }
  checkCoderSupervisor(role) {
    return role.toLowerCase() === Roles[Roles.QCSupervisor].toLowerCase();
  }
  checkCoder(role) {
    return role.toLowerCase() === Roles[Roles.QCCoder].toLowerCase();
  }

  GetActiveRole(): Roles {
    return this.currentUser.activeRole;
  }

  GetActiveProject(): ODPProjectWorkflowViewModel {
    return this.currentUser.activeProject;
  }

  GetActiveTeam(): TeamSelectionViewModel {
    return this.currentUser.activeTeam;
  }

  GetActiveValsSelected(): boolean {
    return this.currentUser.activeValsSelected;
  }

  SetActiveRole(role: Roles) {
    this.authService.setUserActiveRole(role);
  }

  SetActiveProject(project: ODPProjectWorkflowViewModel) {
    this.authService.setUserActiveProject(project);
  }

  SetActiveTeam(team: TeamSelectionViewModel) {
    this.authService.setUserActiveTeam(team);
  }

  SetActiveValsSelected(valsselected: boolean) {
    this.authService.setUserActiveValsSelected(valsselected);
  }

  GetCurrentUserProjects(): ODPProjectWorkflowViewModel[] {
    if (this.currentUser)
      return this.currentUser.projects;
    else
      return [];
  }

  SetCurrentUserProjects(projects: ODPProjectWorkflowViewModel[]) {
    this.currentUser.projects = projects;
  }

  GetCurrentUserProjectsObservable(roleId: number = null): Observable<void> {
    var currentRole = this.currentUser.activeRole;
    if (roleId)
      currentRole = roleId;
    return this.authService.getUserProjects(this.currentUser.id, currentRole);
  }

  GetCurrentUserTeams(): TeamSelectionViewModel[] {
    return this.currentUser.teams;
  }

  SetCurrentUserTeams(teams: TeamSelectionViewModel[]) {
    this.currentUser.teams = teams;
  }

  GetCurrentUserTeamsObservable(roleId: number = null, project: ODPProjectWorkflowViewModel = null): Observable<void> {
    var currentRole = this.currentUser.activeRole;
    if (roleId)
      currentRole = roleId;
    var currentProject = this.currentUser.activeProject;
    if (project)
      currentProject = project;
    return this.authService.getUserTeams(this.currentUser.id, currentRole, currentProject);
  }

  GetCurrentUserRoleIDs(): Roles[] {
    var returnedRoles: Roles[] = [];

    if (this.currentUser) {
      if (this.currentUser.roles) {
        //console.log(this.currentUser.roles);
        for (var i = 0; i < this.currentUser.roles.length; i++) {
          var thisRole = this.currentUser.roles[i].toLowerCase();
          switch (thisRole) {
            case Roles[Roles.Admin].toLowerCase():
              returnedRoles.push(Roles.Admin);
              break;
            case Roles[Roles.ReconciliationSupervisor].toLowerCase():
              returnedRoles.push(Roles.ReconciliationSupervisor);
              break;
            case Roles[Roles.ReconciliationCoder].toLowerCase():
              returnedRoles.push(Roles.ReconciliationCoder);
              break;
            case Roles[Roles.QCSupervisor].toLowerCase():
              returnedRoles.push(Roles.QCSupervisor);
              break;
            case Roles[Roles.QCCoder].toLowerCase():
              returnedRoles.push(Roles.QCCoder);
              break;
            default:
              break;
          }
        }
      }
    }

    return returnedRoles;
  }

  GetRoleData(roleids: number[]) {

  }

  getWestatCoders(coderteams: number[]) {
    return this.accountEndpoint.getWestatCodersEndpoint<User[]>(coderteams);
  }

  getFullWestatCoders() {
    return this.accountEndpoint.getFullWestatCodersEndpoint<User[]>();
  }

  getLoginConfiguration() {
    return this.accountEndpoint.getLoginConfigurationEndpoint<LoginConfig>();
  }
}
